import { h, mount } from "@/lib/dom";
import { button, toast, openModal } from "@/components/ui";
import { voiceService, pbErrorMessage, type VoiceDomain, type VoiceSuggestion } from "@/services";
import { micButton } from "./recorder";
import { uuid } from "@/lib/id";

export interface VoiceColumn<T> {
  label: string;
  render: (payload: T, patch: (p: Partial<T>) => void) => HTMLElement;
}

/**
 * Mikro-Erfassung als kleiner Kopfzeilen-Knopf: Mikro-Symbol + Zähler-Pille,
 * sobald Vorschläge offen sind. Die Vorschläge selbst erscheinen als Popup
 * (nicht mehr fest in der Seite), automatisch sobald neue da sind, oder per
 * Klick auf die Zähler-Pille erneut. Geschrieben werden sie vom Server
 * (Hook) – dieses Bauteil liest nur, editiert inline und wendet beim
 * Übernehmen die eigentliche Fachaktion an (`applyOne`).
 */
export function voiceControl<T extends object>(opts: {
  domain: VoiceDomain;
  columns: VoiceColumn<T>[];
  buildContext: () => unknown;
  applyOne: (payload: T) => Promise<void>;
  onApplied?: () => void;
  /**
   * Läuft über jeden geladenen Vorschlag, bevor er angezeigt wird – Fach für
   * clientseitig aufzulösende Referenzen (z. B. ein von der KI gewählter
   * Index in eine Stundenplan-Kandidatenliste statt eines frei erfundenen
   * Datums). Ohne diese Auflösung würde die KI Datumsarithmetik selbst
   * machen müssen, und genau das ging regelmäßig schief (falsches Jahr).
   */
  normalize?: (payload: T) => T;
}): HTMLElement {
  const batch = uuid();
  let items: VoiceSuggestion<T>[] = [];
  const patchTimers = new Map<string, number>();
  let popupOpen = false;
  let closePopup: (() => void) | null = null;

  const wrap = h("div", { class: "voice-control" });
  const badge = h("button", { type: "button", class: "voice-badge", title: "Vorschläge ansehen" });
  badge.addEventListener("click", () => openPopup());

  async function loadItems(): Promise<void> {
    items = await voiceService.listByDomain<T>(opts.domain);
    if (opts.normalize) items = items.map((i) => ({ ...i, payload: opts.normalize!(i.payload) }));
    renderBadge();
    if (popupOpen) renderPopupBody();
  }

  function renderBadge(): void {
    if (!items.length) {
      badge.style.display = "none";
      return;
    }
    badge.style.display = "";
    badge.textContent = String(items.length);
  }

  /** Nach einem Fehlschlag könnte der Server im Hintergrund trotzdem fertig
   *  geworden sein (siehe hooks.pb.js) – ein paar Mal leise nachschauen statt
   *  den Nutzer zum manuellen Neuladen zu zwingen. */
  async function pollAfterFailure(): Promise<void> {
    const before = items.length;
    for (const delay of [3000, 5000, 8000]) {
      await new Promise((r) => setTimeout(r, delay));
      try {
        await loadItems();
      } catch {
        continue;
      }
      if (items.length !== before) return;
    }
  }

  async function handleAudio(recBtn: HTMLButtonElement, audio: Blob): Promise<void> {
    recBtn.classList.add("is-busy");
    recBtn.disabled = true;
    try {
      const context = { ...(opts.buildContext() as object), existing: items.map((i) => i.payload) };
      await voiceService.record(opts.domain, batch, audio, context);
      await loadItems();
      openPopup();
    } catch (err) {
      toast(pbErrorMessage(err) + " – prüfe gleich nochmal, die Verarbeitung läuft evtl. im Hintergrund weiter.", "error");
      void pollAfterFailure();
    } finally {
      recBtn.classList.remove("is-busy");
      recBtn.disabled = false;
    }
  }

  const recBtn = micButton({
    title: "Per Sprache erfassen",
    onStop: (audio) => void handleAudio(recBtn, audio),
    // getUserMedia() fragt die Berechtigung selbst ab (Browser-Dialog) – ein
    // zweiter eigener "Erbitten"-Schritt gibt es nicht. Nur bei einer bereits
    // ERTEILTEN Ablehnung fragt kein Browser von selbst erneut; die Meldung
    // sagt dann wenigstens, wo man es von Hand ändert, statt pauschal
    // "nicht verfügbar" zu behaupten (die Hardware ist ja meist vorhanden).
    onError: (err) => {
      const name = (err as { name?: string })?.name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        // Auf dem iPhone als Home-Screen-App: Einstellungen → [App-Name] → Mikrofon.
        // Als Website in Safari: Einstellungen → Safari → Kamera & Mikrofon.
        const isPwa = window.matchMedia("(display-mode: standalone)").matches;
        const hint = isPwa
          ? "Einstellungen → [App-Name] → Mikrofon einschalten."
          : "In den Website-Einstellungen des Browsers erlauben.";
        toast(`Mikrofon-Zugriff blockiert – ${hint}`, "error");
      } else if (name === "NotFoundError") {
        toast("Kein Mikrofon gefunden.", "error");
      } else {
        toast("Mikrofon nicht verfügbar – Zugriff erlaubt?", "error");
      }
    },
  });

  mount(wrap, h("span", { class: "voice-control-inner" }, recBtn, badge));

  function schedulePatch(id: string, patch: Partial<T>): void {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    item.payload = { ...item.payload, ...patch };
    window.clearTimeout(patchTimers.get(id));
    patchTimers.set(
      id,
      window.setTimeout(() => void voiceService.updatePayload(id, item.payload as Record<string, unknown>), 500)
    );
  }

  async function acceptItem(item: VoiceSuggestion<T>): Promise<void> {
    try {
      await opts.applyOne(item.payload);
      await voiceService.remove(item.id);
      items = items.filter((i) => i.id !== item.id);
      renderBadge();
      renderPopupBody();
      opts.onApplied?.();
      if (!items.length) closePopup?.();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  async function rejectItem(item: VoiceSuggestion<T>): Promise<void> {
    try {
      await voiceService.remove(item.id);
      items = items.filter((i) => i.id !== item.id);
      renderBadge();
      renderPopupBody();
      if (!items.length) closePopup?.();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  async function acceptAll(): Promise<void> {
    for (const item of [...items]) await acceptItem(item);
  }

  let popupBodyEl: HTMLElement | null = null;

  function renderPopupBody(): void {
    if (!popupBodyEl) return;
    mount(
      popupBodyEl,
      h(
        "div",
        { class: "voice-panel" },
        items.length > 1
          ? h(
              "div",
              { class: "voice-panel-head" },
              button({ label: "Alle übernehmen", size: "sm", variant: "ghost", onClick: () => void acceptAll() })
            )
          : null,
        items.length
          ? h(
              "div",
              { class: "voice-panel-table" },
              ...items.map((item) =>
                h(
                  "div",
                  { class: "voice-panel-row" },
                  ...opts.columns.map((col) =>
                    h(
                      "div",
                      { class: "voice-panel-cell" },
                      col.render(item.payload, (patch) => schedulePatch(item.id, patch))
                    )
                  ),
                  h(
                    "div",
                    { class: "voice-panel-actions" },
                    button({ icon: "check", size: "sm", variant: "primary", title: "Übernehmen", onClick: () => void acceptItem(item) }),
                    button({ icon: "x", size: "sm", variant: "ghost", title: "Verwerfen", onClick: () => void rejectItem(item) })
                  )
                )
              )
            )
          : h("p", { class: "muted text-sm" }, "Keine offenen Vorschläge.")
      )
    );
  }

  function openPopup(): void {
    if (!items.length || popupOpen) return;
    popupOpen = true;
    popupBodyEl = h("div", null);
    renderPopupBody();
    const handle = openModal({
      title: "Vorschläge",
      body: popupBodyEl,
      guardUnsaved: false,
      onClose: () => {
        popupOpen = false;
        popupBodyEl = null;
        closePopup = null;
      },
    });
    closePopup = handle.close;
  }

  renderBadge();
  void loadItems();
  return wrap;
}
